import React from 'react';
import { Link } from 'react-router-dom';
import { trackFreeTrial } from '../utils/tracking';

const Comparisons = () => {
  const competitors = [
    { name: 'ZoomInfo', type: 'Enterprise Data' },
    { name: 'Apollo.io', type: 'Sales Platform' },
    { name: 'Lusha', type: 'Contact Finder' },
    { name: 'Seamless.AI', type: 'AI Search' },
    { name: 'ContactOut', type: 'Chrome Extension' },
  ];

  const features = [
    {
      category: 'Data & Search',
      items: [
        { feature: 'Contact Database Size', knine: '100M+ Records', zoominfo: '100M+', apollo: '275M+', lusha: '100M+', seamless: '100M+', contactout: '30M+' },
        { feature: 'AI-Powered Natural Language Search', knine: true, zoominfo: false, apollo: false, lusha: false, seamless: true, contactout: false },
        { feature: 'Chrome Extension', knine: true, zoominfo: true, apollo: true, lusha: true, seamless: true, contactout: true },
        { feature: 'Email Accuracy Rate', knine: '95%+', zoominfo: '90%+', apollo: '85%+', lusha: '90%+', seamless: '85%+', contactout: '90%+' },
        { feature: 'Real-Time Data Verification', knine: true, zoominfo: true, apollo: false, lusha: true, seamless: false, contactout: false },
        { feature: 'Email Deliverability Check', knine: true, zoominfo: '—', apollo: '—', lusha: '—', seamless: '—', contactout: '—' },
        { feature: 'Phone Type & Carrier ID (Twilio Lookup)', knine: true, zoominfo: '—', apollo: '—', lusha: '—', seamless: '—', contactout: '—' },
      ]
    },
    {
      category: 'Integrations & Workflow',
      items: [
        { feature: 'ATS Integrations (Staffing-Focused)', knine: true, zoominfo: false, apollo: false, lusha: false, seamless: false, contactout: false },
        { feature: 'CRM Integrations', knine: true, zoominfo: true, apollo: true, lusha: true, seamless: true, contactout: false },
        { feature: 'Custom Integrations', knine: true, zoominfo: true, apollo: true, lusha: true, seamless: true, contactout: false },
        { feature: 'CSV Export', knine: true, zoominfo: true, apollo: true, lusha: true, seamless: true, contactout: true },
      ]
    },
    {
      category: 'Specialized Features',
      items: [
        { feature: 'Fee Recovery / Tracker', knine: true, zoominfo: false, apollo: false, lusha: false, seamless: false, contactout: false },
        { feature: 'Auto Enrichment', knine: true, zoominfo: true, apollo: true, lusha: false, seamless: false, contactout: false },
        { feature: 'Built for Recruiting & Sales', knine: true, zoominfo: false, apollo: false, lusha: false, seamless: false, contactout: true },
        { feature: 'Team Analytics', knine: true, zoominfo: true, apollo: true, lusha: false, seamless: false, contactout: false },
      ]
    },
    {
      category: 'Pricing & Value',
      items: [
        { feature: 'Starting Price', knine: '$6,000/yr', zoominfo: '$15,000+/yr', apollo: '$5,400/yr', lusha: '$3,600/yr', seamless: '$6,000+/yr', contactout: '$1,200/yr' },
        { feature: 'Free Trial', knine: true, zoominfo: false, apollo: true, lusha: true, seamless: false, contactout: true },
        { feature: 'Monthly Payment Option', knine: true, zoominfo: false, apollo: true, lusha: true, seamless: true, contactout: true },
        { feature: 'No Long-Term Contract Required', knine: true, zoominfo: false, apollo: true, lusha: true, seamless: false, contactout: true },
      ]
    },
  ];

  const renderCell = (val) => {
    if (val === true) return <span className="check-icon">✓</span>;
    if (val === false) return <span className="cross-icon">✗</span>;
    return <span>{val}</span>;
  };

  return (
    <>
      {/* Header */}
      <section className="hero" style={{ paddingBottom: '40px' }}>
        <div className="container hero-content fade-up">
          <div className="section-badge purple">📊 Market Comparison</div>
          <h1 className="hero-title" style={{ fontSize: '2.75rem' }}>
            See How kNine <span className="text-gradient">Stacks Up</span>
          </h1>
          <p className="hero-subtitle">
            We built kNine for recruiting and sales professionals across every industry. See how we compare to the big dogs in the sourcing space.
          </p>
        </div>
      </section>

      {/* Why kNine */}
      <section style={{ paddingTop: 0 }}>
        <div className="container">
          <div className="feature-grid" style={{ marginBottom: '64px' }}>
            {[
              {
                icon: '🎯',
                title: 'Built for Recruiting & Sales',
                desc: 'kNine is purpose-built for recruiters, staffing firms, sales teams, and sourcing professionals across any industry. Our integrations and workflows are designed for how you actually work.'
              },
              {
                icon: '💰',
                title: 'Better Value',
                desc: 'Get enterprise-grade sourcing at a fraction of the cost. Our $6K/year starting price is a fraction of what ZoomInfo and others charge for similar capabilities.'
              },
              {
                icon: '🐕',
                title: 'AI That Fetches',
                desc: 'Cooper and Roxi use natural language AI search — just describe who you\'re looking for. No complex boolean strings or filters required.'
              },
            ].map((item) => (
              <div key={item.title} className="card" style={{ padding: '32px' }}>
                <div style={{ fontSize: '2rem', marginBottom: '16px' }}>{item.icon}</div>
                <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '10px' }}>
                  {item.title}
                </h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7 }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Comparison Tables */}
          {features.map((section) => (
            <div key={section.category} style={{ marginBottom: '48px' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '16px' }}>
                {section.category}
              </h3>
              <div className="comparison-table-wrapper">
                <table className="comparison-table">
                  <thead>
                    <tr>
                      <th style={{ minWidth: '200px' }}>Feature</th>
                      <th className="highlight" style={{ minWidth: '120px' }}>kNine.ai</th>
                      {competitors.map(c => (
                        <th key={c.name} style={{ minWidth: '110px' }}>{c.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {section.items.map((row) => (
                      <tr key={row.feature}>
                        <td className="feature-name">{row.feature}</td>
                        <td style={{ background: '#f0f7ff' }}><strong>{renderCell(row.knine)}</strong></td>
                        <td>{renderCell(row.zoominfo)}</td>
                        <td>{renderCell(row.apollo)}</td>
                        <td>{renderCell(row.lusha)}</td>
                        <td>{renderCell(row.seamless)}</td>
                        <td>{renderCell(row.contactout)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Unique Advantages */}
      <section className="bg-off-white">
        <div className="container">
          <div className="text-center" style={{ marginBottom: '48px' }}>
            <h2 className="section-title" style={{ fontSize: '1.75rem' }}>
              What Makes kNine <span className="text-gradient">Different</span>
            </h2>
          </div>

          <div className="feature-grid-2" style={{ maxWidth: '800px', margin: '0 auto' }}>
            {[
              {
                icon: '🔄',
                title: 'Fee Recovery & Tracker',
                desc: 'The only sourcing platform with built-in fee recovery detection. Automatically monitor placements and identify revenue opportunities competitors miss entirely.'
              },
              {
                icon: '🧩',
                title: 'Staffing-First ATS Integrations',
                desc: 'Deep integrations with PCRecruiter, Crelate, BigBiller/TE Recruit, and other staffing ATS platforms that general-purpose tools don\'t support.'
              },
              {
                icon: '🤖',
                title: 'True AI Search (Not Just Filters)',
                desc: 'Describe your ideal candidate in plain English. Our AI understands context, skills adjacencies, and implicit requirements — no boolean required.'
              },
              {
                icon: '📈',
                title: 'Auto Enrichment & Insights',
                desc: 'Enterprise plans include automatic data enrichment. Contact records are continuously refreshed, so you always have the latest emails and phone numbers.'
              },
            ].map((item) => (
              <div key={item.title} className="card" style={{ padding: '32px' }}>
                <div style={{ fontSize: '1.75rem', marginBottom: '12px' }}>{item.icon}</div>
                <h4 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '8px' }}>
                  {item.title}
                </h4>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7 }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="container">
          <div className="cta-section">
            <div className="cta-paw" style={{ top: '-20px', left: '-10px', transform: 'rotate(-25deg)' }}>🐾</div>
            <div className="cta-paw" style={{ bottom: '-20px', right: '-10px', transform: 'rotate(15deg)' }}>🐾</div>
            <h2>Ready to Switch to the Top Dog?</h2>
            <p>
              See for yourself why recruiters are switching to kNine. Start your free trial today.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="https://app.knine.ai/register" className="btn btn-white btn-lg" style={{ border: 'none' }} target="_blank" rel="noopener noreferrer" onClick={() => trackFreeTrial('comparisons_cta')}>
                Start Free Trial
              </a>
              <Link to="/pricing" className="btn btn-lg" style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.3)' }}>
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Comparisons;
